import React, { useState, useEffect } from 'react';
import { EmptyState, Modal } from '../../components';
import { getCurrentUser } from '../../data/session';
import { getGroupSessions } from '../../services/group-session-service';

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

const isPast = (session: any) => {
  const end = new Date(session.scheduledAt).getTime() + (session.duration || 60) * 60 * 1000;
  return end < Date.now();
};

/* ── Detail Modal ─────────────────────────────────────────────────── */
const SessionDetailModal: React.FC<{ session: any; onClose: () => void }> = ({ session, onClose }) => {
  const mentorName = session.mentorId?.name || session.mentorName || 'TBA';
  const past = isPast(session);
  const cancelled = session.status === 'cancelled';
  const participants: any[] = session.participants ?? [];
  const attendees: any[] = session.attendees ?? [];

  const mergedParticipants = participants.length
    ? participants.map((p: any) => {
        const att = attendees.find((a: any) => String(a.userId) === String(p.userId));
        return { name: p.name, status: att?.status ?? 'invited' };
      })
    : attendees.map((a: any) => ({ name: a.name, status: a.status }));

  const statusColor: Record<string, string> = {
    present:   'bg-success/15 text-success',
    absent:    'bg-danger/15 text-danger',
    confirmed: 'bg-chart/15 text-chart',
    invited:   'bg-muted/15 text-muted',
  };

  return (
    <Modal isOpen onClose={onClose} title={session.title} className="max-w-lg">
      <div className="space-y-5">
        {/* Meta */}
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-muted">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
            </svg>
            <span className="text-text">{fmtDate(session.scheduledAt)} at {fmtTime(session.scheduledAt)}</span>
          </div>
          <div className="flex items-center gap-2 text-muted">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-text">{session.duration} minutes</span>
          </div>
          <div className="flex items-center gap-2 text-muted">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
            </svg>
            <span className="text-text">{mentorName}</span>
          </div>
        </div>

        {/* Agenda */}
        {session.agenda && (
          <div>
            <p className="text-xs text-muted uppercase tracking-wider font-semibold mb-1.5">Agenda</p>
            <p className="text-sm text-text whitespace-pre-wrap">{session.agenda}</p>
          </div>
        )}

        {/* Participants */}
        {mergedParticipants.length > 0 && (
          <div>
            <p className="text-xs text-muted uppercase tracking-wider font-semibold mb-2">
              Participants ({mergedParticipants.length})
            </p>
            <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
              {mergedParticipants.map((p: any, i: number) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-text">{p.name || 'Unknown'}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor[p.status] ?? 'bg-muted/15 text-muted'}`}>
                    {p.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Join */}
        {!past && !cancelled && session.meetLink && (
          <a
            href={session.meetLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-2.5 bg-chart text-white text-sm font-medium rounded-lg hover:bg-chart/90 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
            </svg>
            Join Session
          </a>
        )}
        {!past && !cancelled && !session.meetLink && (
          <p className="text-xs text-muted text-center">Meeting link will be shared soon.</p>
        )}
      </div>
    </Modal>
  );
};

/* ── Session Card ─────────────────────────────────────────────────── */
const SessionCard: React.FC<{ session: any; onDetails: (s: any) => void }> = ({ session, onDetails }) => {
  const mentorName = session.mentorId?.name || session.mentorName || 'TBA';
  const past = isPast(session);
  const cancelled = session.status === 'cancelled';
  const participantCount = session.participants?.length ?? session.attendees?.length ?? 0;

  return (
    <div className="card flex flex-col gap-4">
      {/* Top */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-text font-semibold text-base leading-snug">{session.title}</h3>
          <p className="text-muted text-sm mt-0.5">{mentorName}</p>
        </div>
        {cancelled ? (
          <span className="flex-shrink-0 px-2.5 py-0.5 rounded-full text-xs font-medium bg-danger/15 text-danger">Cancelled</span>
        ) : past ? (
          <span className="flex-shrink-0 px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted/15 text-muted">Ended</span>
        ) : (
          <span className="flex-shrink-0 px-2.5 py-0.5 rounded-full text-xs font-medium bg-chart/15 text-chart">Upcoming</span>
        )}
      </div>

      {/* Date / time / duration */}
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-muted">
        <span className="flex items-center gap-1.5">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
          </svg>
          {fmtDate(session.scheduledAt)}
        </span>
        <span className="flex items-center gap-1.5">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {fmtTime(session.scheduledAt)} · {session.duration} min
        </span>
        <span className="flex items-center gap-1.5">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
          </svg>
          {participantCount} participant{participantCount !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Agenda preview */}
      {session.agenda && (
        <p className="text-sm text-muted border-t border-line pt-3 line-clamp-2">{session.agenda}</p>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 border-t border-line pt-3">
        {!past && !cancelled && session.meetLink && (
          <a
            href={session.meetLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-chart text-white text-sm font-medium rounded-lg hover:bg-chart/90 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
            </svg>
            Join Session
          </a>
        )}
        <button
          onClick={() => onDetails(session)}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-muted border border-line rounded-lg hover:text-text hover:border-chart/30 transition-colors"
        >
          Details
        </button>
      </div>
    </div>
  );
};

/* ── Main Page ────────────────────────────────────────────────────── */
const GroupSessions: React.FC = () => {
  const user = getCurrentUser();
  const role = user.role;

  const [sessions, setSessions]     = useState<any[]>([]);
  const [isLoading, setIsLoading]   = useState(true);
  const [detailSession, setDetail]  = useState<any | null>(null);

  useEffect(() => {
    getGroupSessions()
      .then(data => setSessions(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) {
    return (
      <div className="section-gap">
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-chart border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  /* Founder + Mentor: card grid with upcoming / past split */
  if (role === 'founder' || role === 'mentor') {
    const upcoming = sessions.filter(s => !isPast(s) && s.status !== 'cancelled');
    const past     = sessions.filter(s => isPast(s) || s.status === 'cancelled');

    const subtitle = role === 'mentor'
      ? 'Sessions you are leading.'
      : 'Sessions scheduled for your cohort.';

    return (
      <div className="section-gap">
        <div>
          <h1 className="heading text-2xl">Group Sessions</h1>
          <p className="text-muted mt-1">{subtitle}</p>
        </div>

        {sessions.length === 0 ? (
          <EmptyState title="No sessions yet" description="Group sessions will appear here once scheduled." />
        ) : (
          <>
            {upcoming.length > 0 && (
              <div className="space-y-3">
                <h2 className="text-sm font-semibold text-muted uppercase tracking-wider">Upcoming</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {upcoming.map(s => <SessionCard key={s._id} session={s} onDetails={setDetail} />)}
                </div>
              </div>
            )}
            {past.length > 0 && (
              <div className="space-y-3">
                <h2 className="text-sm font-semibold text-muted uppercase tracking-wider">Past Sessions</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 opacity-70">
                  {past.map(s => <SessionCard key={s._id} session={s} onDetails={setDetail} />)}
                </div>
              </div>
            )}
          </>
        )}

        {detailSession && (
          <SessionDetailModal session={detailSession} onClose={() => setDetail(null)} />
        )}
      </div>
    );
  }

  /* Admin: simple list */
  const STATUS_BADGE: Record<string, string> = {
    scheduled:   'bg-chart/15 text-chart',
    in_progress: 'bg-warning/15 text-warning',
    completed:   'bg-success/15 text-success',
    cancelled:   'bg-danger/15 text-danger',
  };

  return (
    <div className="section-gap">
      <div>
        <h1 className="heading text-2xl">Group Sessions</h1>
        <p className="text-muted mt-1">All group sessions across the program.</p>
      </div>

      {sessions.length === 0 ? (
        <EmptyState title="No sessions yet" description="No group sessions scheduled at this time." />
      ) : (
        <div className="space-y-3">
          {sessions.map(session => {
            const mentorName = session.mentorId?.name || session.mentorName || 'Unknown';
            const participantCount = session.participants?.length ?? session.attendees?.length ?? 0;
            return (
              <button
                key={session._id}
                onClick={() => setDetail(session)}
                className="card w-full text-left hover:border-chart/30 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <h3 className="heading text-base truncate">{session.title}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[session.status] ?? 'bg-muted/15 text-muted'}`}>
                        {session.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted">
                      <span>{fmtDate(session.scheduledAt)} at {fmtTime(session.scheduledAt)}</span>
                      <span>{session.duration} min</span>
                      <span>{mentorName}</span>
                      <span>{participantCount} participant{participantCount !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                  <svg className="w-5 h-5 text-muted flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {detailSession && (
        <SessionDetailModal session={detailSession} onClose={() => setDetail(null)} />
      )}
    </div>
  );
};

export default GroupSessions;
